/**
 * `WorkflowScriptRunner.ts` — web port of Unity's
 * `Models/WorkflowModel/WorkflowScriptRunner.cs`.
 *
 * Plays a workflow's self-contained scripts straight from the workflow document. Unity
 * materializes each script into a transient `ActionData` and hands it to the scene replay engine;
 * the web port has no replay engine, so this runner executes the steps directly:
 *   - `say` steps route the verbatim line into the realtime session as an "INTERNAL SAY:" message
 *     (the base prompt's "# Internal Speech Messages" section makes Spark speak it verbatim), then
 *     await the response finishing. The line is flattened out of Markdown first
 *     (`markdownSpeech.ts`) — "verbatim" is what makes a `**bold**` marker something to pronounce
 *     rather than something to interpret;
 *   - tool steps await `AgentToolExecutorModel.executeAsync(toolId, argsJson)` and any
 *     tool-specific playback effect (currently finite highlight pulses);
 *   - a following `do_not_wait` marker lets the preceding step overlap later work, matching Unity.
 *
 * Revert (`revert: on_action_finish` / `on_scene_change`) is snapshot-and-restore, ported in
 * `WorkflowRevertState.ts`: the pre-tool state is captured before every tool step and put back
 * either when the script ends or when the scene it ran in is left. Unity gets this from the replay
 * engine's `PreviewRestoreState`; the runner owns it here because the web has no replay engine.
 *
 * Web deviations (deliberate):
 *   - Unity ALSO force-reverts every script on play-mode exit, whatever its declared mode, because
 *     its capture is unconditional while play mode is on. Here only a declared mode reverts, so a
 *     `revert: none` script still means "these changes are the point, keep them".
 *   - No pause/resume yet (Unity rides the replay engine's pause) — stop only.
 */

import { Emitter } from "@core/events/emitter";
import { AgentToolNames } from "@ai/registry/agentToolNames";
import { isScriptContextTool } from "@ai/registry/scriptCommandCatalog";
import { beginWorkflowPerformanceOperation } from "@models/project/SceneNavigationDiagnosticLogger";
import type {
  WorkflowDocument,
  WorkflowScript,
  WorkflowScriptRevert,
  WorkflowStep,
} from "./WorkflowDocument";
import { markdownToSpeechText } from "@models/utils/markdownSpeech";
import { resolveSayText } from "./WorkflowDocument";
import { resolveWorkflowText } from "./workflowTextTokens";
import type { WorkflowMediaControlResult } from "./IWorkflowRunModel";
import {
  WorkflowRevertLedger,
  type WorkflowRevertDependencies,
} from "./WorkflowRevertState";

const K_ACTION_ID_PREFIX = "wf:";
/** Upper bound on one say-step's speech (safety net when response events misbehave). */
const K_SAY_TIMEOUT_MS = 60_000;

/** `wait_for` delay used when none is authored, or an unusable one is. */
const K_DEFAULT_WAIT_SECONDS = 3;
/** Ceiling on an authored `delaySeconds` — five minutes is already a typo. */
const K_MAX_WAIT_SECONDS = 300;
/**
 * Ceiling on a `wait_for` user_response. A learner who walks away must not
 * hold the script — and its pending revert — open for the life of the tab.
 */
const K_USER_RESPONSE_TIMEOUT_MS = 10 * 60_000;
/** How often a parked wait re-checks its run for cancellation. */
const K_CANCEL_POLL_MS = 100;
/** How long `play_video` waits for the step's media surface to mount. */
const K_VIDEO_READY_TIMEOUT_MS = 10_000;

/** A cancellable sleep. Resolves early (rather than rejecting) when aborted. */
function delayAsync(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise<void>((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const timer = window.setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    function onAbort(): void {
      window.clearTimeout(timer);
      resolve();
    }
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

const K_VALID_TOOL_IDS = new Set(Object.values(AgentToolNames).map((id) => id.toLowerCase()));

/** lowercase wire id → canonical wire id (Unity `FromId` → `GetId` round trip). */
const K_CANONICAL_TOOL_IDS = new Map(
  Object.values(AgentToolNames).map((id) => [id.toLowerCase(), id] as const),
);

/** What the runner needs from the AI harness (provided by the orchestrator). */
export interface WorkflowScriptHost {
  /** Executes one tool step; resolves with the "ok:"/"error:" tool result. */
  executeToolAsync(toolId: string, args: Record<string, unknown>): Promise<string>;
  /** Speaks a verbatim line through the session ("INTERNAL SAY:" message). */
  sayVerbatim(text: string): void;
  /**
   * Resolves TRUE once the session can actually carry a say, FALSE on the
   * host's own timeout or when `signal` aborts.
   *
   * ⚠ A say sent before the session is ready is dropped SILENTLY — no error,
   * no retry, nobody behind it to notice. Three windows on a reload: no
   * session object yet (the orchestrator awaits three prompt-catalog fetches
   * before it even connects), the socket not yet open, and — the one that
   * looks connected — the ~450 ms between the socket opening and the server's
   * `session.created`, where the proxy drops the item AND the instructions
   * carrying the `INTERNAL SAY:` contract have not been flushed.
   *
   * A viewer-role learner meets all three: their autostart fires
   * SYNCHRONOUSLY on `onLoadProjectCompleted` with no settle dwell, so the run
   * routinely starts before the session is up and step one narrates nothing.
   *
   * Optional: a headless host has no session, and one that does not implement
   * it keeps the old fire-and-forget behaviour.
   */
  waitForSpeechReadyAsync?(signal: AbortSignal): Promise<boolean>;
  /** Resolves when the active response (the spoken say line) finishes, or on timeout. */
  waitForResponseIdleAsync(timeoutMs: number): Promise<void>;
  /** Resolves when playback-specific effects started by a successful tool have finished. */
  waitForToolEffectsIdleAsync?(
    toolId: string,
    args: Record<string, unknown>,
  ): Promise<void>;
  /**
   * Starts the owning step's video through the mounted media surface.
   * `unavailable` is transient while its URL resolves and element mounts.
   */
  playCurrentStepVideo?(stepName: string): WorkflowMediaControlResult;
  /**
   * How many turns the learner has taken this session — spoken or typed.
   *
   * Read alongside {@link waitForUserTurnAsync} so a wait can tell whether the
   * learner ALREADY answered. A script asks its question in a `say` step, and
   * that step is awaited on its own before the loop reaches the wait — so a
   * learner who answers over the question does it while nothing is listening.
   * Counting turns continuously is what closes that window; a listener attached
   * at the wait, however early, cannot.
   */
  getUserTurnCount?(): number;
  /**
   * Resolves on the learner's next turn — spoken or typed — or on timeout, or
   * when `signal` aborts. Backs a `wait_for` step with `trigger: user_response`.
   *
   * Resolves immediately when {@link getUserTurnCount} has already moved past
   * `sinceTurnCount`: that is the learner having answered during the step
   * before this one.
   *
   * Resolves TRUE only when a real turn arrived, so the caller can tell that
   * case from a timeout or a cancel: a turn has a reply coming and the script
   * must let it finish before speaking again, while the other two do not.
   *
   * Optional because a headless host has no session to hear a turn on; the
   * runner degrades to a short pause rather than hanging when it is absent.
   */
  waitForUserTurnAsync?(
    sinceTurnCount: number,
    timeoutMs: number,
    signal: AbortSignal,
  ): Promise<boolean>;
  /**
   * The signed-in learner's first name, for the `<user name>` token a `say:`
   * line may carry. Empty when nothing is known.
   *
   * On the host rather than injected separately because this is the runner's
   * one seam to the session, and the name is a runtime fact it has no other way
   * to reach — the same reason `getUserTurnCount` lives here. Optional so a
   * headless host degrades to leaving the token unresolved-to-empty rather than
   * failing.
   */
  getUserFirstName?(): string;
  /**
   * Cuts off whatever Spark is saying right now (barge-in), whether that is a
   * script's say line or an answer to a question the learner asked.
   *
   * A say line has no handle once sent — `sayVerbatim` posts a user message and
   * the model speaks the reply — so the only way to silence one is to cancel
   * the RESPONSE. Doing that also un-parks {@link waitForResponseIdleAsync},
   * which is what lets a cancelled script unwind (and revert) in the moment
   * instead of at its 60-second say timeout.
   */
  stopSpeaking(): void;
}

export type WorkflowScriptPlaybackStatus = "playing" | "finished" | "stopped" | "failed";

export interface WorkflowScriptPlaybackState {
  readonly actionId: string;
  readonly scriptId: string;
  readonly stepName: string;
  readonly trigger: string;
  /** 1-based index of the executing step (0 before the first step). */
  readonly stepIndex: number;
  readonly stepCount: number;
  readonly stepLabel: string;
  readonly status: WorkflowScriptPlaybackStatus;
  /**
   * This emit is a `wait_for` step parked on the LEARNER's next turn: the
   * lesson has stopped and handed them the floor.
   *
   * True on exactly one emit — that step's `playing` — and false on every
   * other, including its own script's terminal one. A consumer that needs to
   * know the wait is OVER reads the next `playing` or the terminal emit rather
   * than a second signal here.
   *
   * It exists because the shell CAN hide Spark's composer while a step's script
   * plays (`WorkflowPlaybackView.isAiInputHeld`), and this is the one moment in
   * a script where that would be a dead end: the script would be waiting for a
   * reply on a surface the learner cannot see. That hide is gated OFF as
   * shipped (2026-09-01), so today this signal releases a latch nobody is
   * acting on — it stays wired because the flag is one line, and because
   * turning it back on with the escape missing is the one way to strand a
   * learner outright.
   */
  readonly isAwaitingUserResponse: boolean;
}

/** Deterministic replay id for a workflow script (`wf:<workflowUid>:<scriptId>`). */
export function buildScriptActionId(
  document: WorkflowDocument | null,
  script: WorkflowScript | null,
): string {
  const workflowKey = document?.uid?.trim() ? document.uid.trim() : "workflow";
  return `${K_ACTION_ID_PREFIX}${workflowKey}:${script?.id?.trim() ?? ""}`;
}

/** True when an action id belongs to a workflow script. */
export function isWorkflowScriptActionId(actionId: string | null | undefined): boolean {
  return !!actionId && actionId.startsWith(K_ACTION_ID_PREFIX);
}

/** Extracts the script id back out of a workflow-script action id, or null. */
export function tryGetScriptIdFromActionId(actionId: string | null | undefined): string | null {
  if (!actionId || !isWorkflowScriptActionId(actionId)) return null;
  const lastSeparator = actionId.lastIndexOf(":");
  return lastSeparator > 0 && lastSeparator + 1 < actionId.length
    ? actionId.substring(lastSeparator + 1)
    : null;
}

interface ActiveScriptRun {
  cancelled: boolean;
  /**
   * Tool steps already dispatched and not yet settled. A revert must not run
   * while one is outstanding: its write has not landed, so restoring first
   * would leave behind exactly the change the revert exists to undo.
   */
  pendingToolCount: number;
  readonly revertMode: WorkflowScriptRevert;
  readonly ledger: WorkflowRevertLedger | null;
  /** Idempotence for the two places that can settle it — a stop, and the loop. */
  revertSettled: boolean;
  /**
   * Resolves once this run has handed its captured state to the immediate
   * restore chain or the pending scene-change ledger. Project unload waits on
   * this when a tool write was already in flight at teardown.
   */
  readonly revertSettlement: Promise<void>;
  readonly resolveRevertSettlement: () => void;
}

export class WorkflowScriptRunner {
  /** Progress feed for the Workflow panel (per-step + lifecycle transitions). */
  readonly onScriptStateChanged = new Emitter<WorkflowScriptPlaybackState>();

  private readonly m_host: WorkflowScriptHost;
  private readonly m_activeScripts = new Map<string, ActiveScriptRun>();
  private readonly m_revert: WorkflowRevertDependencies | null;
  /** `revert: on_scene_change` bags, waiting for their scene to be left. */
  private readonly m_pendingSceneChangeReverts = new WorkflowRevertLedger();
  /**
   * Every restore runs on one chain, and a script waits for it before its
   * first step. Without that ordering a Next that stops a reverting script and
   * lands on a step whose on-start script captures immediately would snapshot
   * the PREVIOUS script's changes as the state to preserve.
   */
  private m_revertChain: Promise<void> = Promise.resolve();
  private m_lastActiveSceneId: string | null = null;
  private readonly m_offFns: Array<() => void> = [];

  /**
   * @param revert Scene state needed to capture and restore script changes.
   * Optional so a headless host (tests, the MCP bridge) can run scripts
   * without a project; scripts then play with revert disabled.
   */
  constructor(host: WorkflowScriptHost, revert: WorkflowRevertDependencies | null = null) {
    this.m_host = host;
    this.m_revert = revert;
    if (!revert) return;

    this.m_lastActiveSceneId = revert.project.activeSceneId;
    this.m_offFns.push(
      revert.project.onActiveSceneChanged.on((scene) => this.handleActiveSceneChanged(scene?.id ?? null)),
      revert.project.registerProjectUnloadBarrier(() => this.prepareProjectUnloadAsync()),
    );
  }

  dispose(): void {
    for (const off of this.m_offFns) off();
    this.m_offFns.length = 0;
  }

  get hasRunningScripts(): boolean {
    return this.m_activeScripts.size > 0;
  }

  isScriptRunning(actionId: string): boolean {
    return this.m_activeScripts.has(actionId);
  }

  /** Stops every workflow script currently playing (play-mode exit, run stop). */
  stopAllRunningScripts(): void {
    for (const run of this.m_activeScripts.values()) {
      run.cancelled = true;

      // Revert NOW rather than when the loop unwinds. The loop's `finally` is
      // still the normal path, but it cannot run while the script is parked on
      // a say — up to 60 s — and by then the learner is two steps away with
      // the previous script's changes still applied to their scene.
      //
      // Unless a tool step is in flight: that write has not landed yet, so a
      // restore would be overwritten by the very change it is undoing. Those
      // runs settle in the `finally`, which is one tool call away.
      if (run.pendingToolCount === 0) this.settleRunRevert(run);
    }

    // Cancelled runs stop counting as "running" IMMEDIATELY, not when their
    // in-flight step await unwinds (a pending say can hold that for up to
    // 60 s). A zombie entry here made Next's skip-branch fire on a first
    // press, and made a re-entered step's on-start replay refuse with
    // `script_already_playing`. The run objects keep their cancelled flag —
    // each playScriptAsync loop holds a direct reference.
    this.m_activeScripts.clear();
  }

  /**
   * Stops script work and restores every queued `on_scene_change` bag before
   * the project model drops its scene/entity state.
   *
   * The normal `onActiveSceneChanged(null)` notification is too late for this:
   * project unload has already cleared the maps the restore reads. The project
   * model awaits this method through its unload-barrier seam while all captured
   * rows and the cloud write context are still available.
   */
  private async prepareProjectUnloadAsync(): Promise<void> {
    const activeRuns = [...this.m_activeScripts.values()];
    this.stopAllRunningScripts();
    this.interruptSpeech();

    // A run with a tool write already in flight settles its ledger from the
    // loop's `finally`, after that write lands. Restoring before then would let
    // the late tool overwrite its own revert.
    await Promise.all(activeRuns.map((run) => run.revertSettlement));
    await this.m_revertChain;

    if (!this.m_pendingSceneChangeReverts.hasRestorableState) return;
    const pending = this.m_pendingSceneChangeReverts;
    this.queueRevert((deps) => pending.restoreAllAsync(deps));
    await this.m_revertChain;
  }

  /**
   * Silences Spark mid-sentence. Separate from {@link stopAllRunningScripts}
   * because the two answer different questions: a step change must stop the
   * outgoing narration whether or not a SCRIPT was the thing narrating — the
   * learner may simply have asked a question and moved on while it was being
   * answered.
   */
  interruptSpeech(): void {
    try {
      this.m_host.stopSpeaking();
    } catch (error) {
      // Barge-in is best-effort: a disconnected session must not break a move.
      console.warn("[WorkflowScriptRunner] Could not stop Spark speaking:", error);
    }
  }

  /**
   * Executes a script's steps in order. Returns an "ok: ..." / "error: ..." result string
   * following the tool-result convention (strings match Unity's `PlayScriptAsync`).
   */
  async playScriptAsync(
    document: WorkflowDocument | null,
    step: WorkflowStep | null,
    script: WorkflowScript | null,
  ): Promise<string> {
    if (!document || !step || !script) {
      return "error: script_unavailable";
    }

    // Validate every step up-front (Unity `TryBuildActionData` fails before playing).
    const validationError = WorkflowScriptRunner.validateSteps(script);
    if (validationError) return validationError;

    const actionId = buildScriptActionId(document, script);
    if (this.m_activeScripts.has(actionId)) {
      return `ok: script_already_playing scriptId=${script.id}`;
    }

    const revertMode = this.resolveRevertMode(script);
    const ledger = revertMode === "none" ? null : new WorkflowRevertLedger();
    // A revert still settling from the script this one replaces has to land
    // before the first capture, or it would be captured as the "before" state.
    if (ledger) await this.m_revertChain;

    let resolveRevertSettlement = (): void => {};
    const revertSettlement = new Promise<void>((resolve) => {
      resolveRevertSettlement = resolve;
    });
    const run: ActiveScriptRun = {
      cancelled: false,
      pendingToolCount: 0,
      revertMode,
      ledger,
      revertSettled: false,
      revertSettlement,
      resolveRevertSettlement,
    };
    this.m_activeScripts.set(actionId, run);
    const stepCount = script.steps.length;
    // The loop below shadows `step` with the SCRIPT step it is executing, so
    // the owning WORKFLOW step — which `<step_instructions>` resolves against —
    // is captured here under its own name.
    const owningStep = step;
    const overlappedTasks: Promise<void>[] = [];
    let activeSpeechTask: Promise<void> | null = null;
    const emit = (
      stepIndex: number,
      stepLabel: string,
      status: WorkflowScriptPlaybackStatus,
      isAwaitingUserResponse = false,
    ) =>
      this.onScriptStateChanged.emit({
        actionId,
        scriptId: script.id,
        stepName: step.name,
        trigger: script.trigger,
        stepIndex,
        stepCount,
        stepLabel,
        status,
        isAwaitingUserResponse,
      });

    emit(0, "", "playing");
    try {
      // Two step-boundary snapshots of the turn counter: a `wait_for` asks
      // about the one from before the step that PRECEDED it, which is the
      // window in which its question was being spoken.
      let turnCountBeforePreviousStep = this.readUserTurnCount();
      let turnCountBeforeThisStep = turnCountBeforePreviousStep;

      for (let i = 0; i < script.steps.length; i++) {
        turnCountBeforePreviousStep = turnCountBeforeThisStep;
        turnCountBeforeThisStep = this.readUserTurnCount();

        if (run.cancelled) {
          emit(i, "", "stopped");
          return `ok: script_stopped scriptId=${script.id}`;
        }

        const step = script.steps[i];
        const stepNumber = i + 1;
        if (WorkflowScriptRunner.isDoNotWaitStep(step)) continue;

        const shouldOverlap = WorkflowScriptRunner.shouldOverlapStep(script, i);
        if (step.isSay) {
          // Three hops, and the position of each is load-bearing.
          //
          // 1. `<step_instructions>` defers to the OWNING step's authored copy,
          //    so the panel and the spoken line cannot drift. Resolved here
          //    rather than at parse time: the author can edit the instructions
          //    after the script is written, and a value baked in at parse would
          //    be the stale copy the token exists to prevent.
          // 2. Those instructions are MARKDOWN, and the line reaches the model
          //    under a contract that says to speak it VERBATIM — so the markers
          //    have to come off before it is handed over, or they are read out
          //    ("asterix, asterix, part"; `markdownSpeech.ts` carries the whole
          //    argument, including why this is not a prompt rule). It runs on a
          //    literal `say:` line too: an author typing `**this**` into one
          //    means the same thing by it.
          // 3. Runtime tokens run LAST, and for two separate reasons. The
          //    instructions are themselves authored prose, so a `<user name>`
          //    written in them has to be substituted too — running this before
          //    hop 1 would only ever see the token's brackets. And running it
          //    before hop 2 would put a markup parser over the learner's own
          //    NAME, which is data rather than authored text.
          const sayText = resolveWorkflowText(
            markdownToSpeechText(resolveSayText(step.sayText, owningStep)),
            { userFirstName: this.m_host.getUserFirstName?.() ?? "" },
          );
          // Tested on the RESULT, not on the source. Instructions the step does
          // not have resolve to nothing — and so does a line that was only
          // markup, a `---` rule being the reachable case. An empty
          // `INTERNAL SAY:` would leave the model improvising a line nobody
          // wrote, so skip the step instead; validate() warns about the first
          // case, so the author is told rather than the run silently narrating.
          if (sayText.trim().length === 0) continue;

          // Unity never overlaps two SAY steps with each other, even when the
          // first SAY overlaps intervening non-speech work.
          if (activeSpeechTask) await activeSpeechTask;
          emit(stepNumber, `Say "${sayText}"`, "playing");
          const speechTask = this.playSayStepAsync(run, sayText);
          activeSpeechTask = speechTask;
          const trackedSpeechTask = speechTask.finally(() => {
            if (activeSpeechTask === speechTask) activeSpeechTask = null;
          });
          if (shouldOverlap) overlappedTasks.push(trackedSpeechTask);
          else await trackedSpeechTask;
          continue;
        }

        const toolId = K_CANONICAL_TOOL_IDS.get(step.tool.trim().toLowerCase()) ?? step.tool;

        // `wait_for` is the runner's own command, not a registered tool: it
        // pauses the LESSON, which is a notion playback has and a chat turn
        // does not. Handled here for the same reason `do_not_wait` is —
        // dispatching it to the executor would answer `tool_not_supported`.
        //
        // A pending say is awaited first: waiting for the learner while Spark
        // is still mid-sentence would ask them to answer a question they have
        // not heard the end of.
        if (WorkflowScriptRunner.isWaitForStep(step)) {
          const waitArgs = step.args ?? {};
          emit(
            stepNumber,
            WorkflowScriptRunner.describeWait(waitArgs),
            "playing",
            // A `delay` wait is still the lesson running; only a wait on the
            // LEARNER hands them the floor, which is what the shell reads to
            // bring Spark's composer back mid-script.
            WorkflowScriptRunner.readWaitTrigger(waitArgs) === "user_response",
          );

          // Waits past the count from BEFORE the previous step, so a learner
          // who answered over the question the previous `say` was asking has
          // already satisfied it. Attaching a listener here — however early —
          // cannot see that turn: the say was awaited in its own iteration.
          const waitTask = this.playWaitStepAsync(run, waitArgs, turnCountBeforePreviousStep);
          if (activeSpeechTask) await activeSpeechTask;

          // `do_not_wait` applies to whatever step precedes it, with no
          // carve-out for this one — the reference says so, and a script author
          // has a real use for it ("ask them to look, and highlight it while
          // they do"). Honoured the same way the say and tool branches do.
          if (shouldOverlap) overlappedTasks.push(waitTask);
          else await waitTask;
          continue;
        }

        // `play_video` is narrower than the Guide's live `control_media` tool:
        // it can only start the video belonging to this script's own workflow
        // step. The media element mounts asynchronously after step activation,
        // so the runner owns the short readiness wait instead of turning a
        // routine first-render race into a failed command.
        if (WorkflowScriptRunner.isPlayVideoStep(step)) {
          emit(stepNumber, "Play video", "playing");
          const playTask = this.playVideoStepAsync(run, owningStep.name);
          if (shouldOverlap) overlappedTasks.push(playTask);
          else await playTask;
          continue;
        }

        emit(stepNumber, `${stepNumber}. ${toolId}`, "playing");
        // Steps are authored as YAML, so arguments arrive as a real object —
        // no JSON string to re-parse (and no parse failure to handle) here.
        const args = step.args ?? {};
        // Before the tool runs: what it is about to overwrite is the only
        // moment the pre-script state is still readable.
        if (ledger && this.m_revert) ledger.capture(toolId, args, this.m_revert);
        const toolTask = trackToolStep(run, this.playToolStepAsync(script.id, stepNumber, toolId, args));
        if (shouldOverlap) overlappedTasks.push(toolTask);
        else await toolTask;
      }

      // `do_not_wait` overlaps a step with later work, but the script itself
      // remains active until every overlapped step has settled.
      if (overlappedTasks.length > 0) {
        await Promise.all(overlappedTasks);
      }

      // A cancel that lands while the FINAL step is awaiting exits the loop
      // without hitting the top-of-loop check — it must still terminate as
      // "stopped", never "finished": a cancelled run must not report the
      // same terminal state as one that played to the end,
      // and a stale finish from a cancelled script would complete-and-advance
      // a step the learner deliberately navigated away from (or back onto).
      if (run.cancelled) {
        emit(stepCount, "", "stopped");
        return `ok: script_stopped scriptId=${script.id}`;
      }

      emit(stepCount, "", "finished");
      return `ok: script_played scriptId=${script.id} step="${step.name}" steps=${stepCount}`;
    } catch (e) {
      console.warn(`[WorkflowScriptRunner] Script '${script.id}' playback failed:`, e);
      emit(0, "", "failed");
      return `error: script_playback_failed scriptId=${script.id}`;
    } finally {
      // Settles however the script ended — finished, stopped mid-step by Next
      // or Back, or thrown. A script the learner walked out of is exactly the
      // one whose half-applied changes most need putting back. A stop may have
      // settled it already (see `stopAllRunningScripts`), which is why this
      // goes through the idempotent seam rather than calling `settleRevert`.
      this.settleRunRevert(run);
      // Only remove the entry when it is still OURS: a cancelled run's map
      // slot may have been cleared and re-occupied by a restart of the same
      // script, and deleting blindly would evict the newer, live run.
      if (this.m_activeScripts.get(actionId) === run) {
        this.m_activeScripts.delete(actionId);
      }
    }
  }

  /**
   * Speaks one authored line, once the session can actually carry it.
   *
   * The wait is here rather than around the RUN because a say is the only
   * thing in a script that needs the model: a step's scene, media and
   * instructions should render the moment the learner arrives, and a workflow
   * with no say steps must not wait for an AI session at all.
   *
   * ⚠ Two things the wait makes newly possible, both handled below. The
   * learner can navigate away while we are parked, so the run is re-checked
   * before the line is sent — otherwise the step they just LEFT narrates over
   * the one they are on. And a say that was never sent has no response coming,
   * so waiting for one would burn the idle grace (5 s) per step for nothing;
   * that dead air is what the pre-fix behaviour spent on every dropped line.
   */
  private async playSayStepAsync(run: ActiveScriptRun, text: string): Promise<void> {
    const waitForSpeechReady = this.m_host.waitForSpeechReadyAsync;
    if (waitForSpeechReady) {
      let ready = false;
      await this.raceCancellation(run, async (signal) => {
        ready = await waitForSpeechReady.call(this.m_host, signal);
      });
      if (run.cancelled) return;
      if (!ready) {
        console.warn(
          `[WorkflowScriptRunner] Say skipped — no ready realtime session: "${text}"`,
        );
        return;
      }
    }
    this.m_host.sayVerbatim(text);
    await this.m_host.waitForResponseIdleAsync(K_SAY_TIMEOUT_MS);
  }

  private async playToolStepAsync(
    scriptId: string,
    stepNumber: number,
    toolId: string,
    args: Record<string, unknown>,
  ): Promise<void> {
    const endPerformanceOperation = beginWorkflowPerformanceOperation("workflow tool", {
      scriptId,
      stepNumber,
      toolId,
    });
    try {
      const result = await this.m_host.executeToolAsync(toolId, args);
      if (!WorkflowScriptRunner.isFailureResult(result)) {
        await this.m_host.waitForToolEffectsIdleAsync?.(toolId, args);
        return;
      }

      console.warn(
        `[WorkflowScriptRunner] Script '${scriptId}' step ${stepNumber} (${toolId}) failed: ${result}`,
      );
      // Unity's replay engine keeps playing after a failed state; match that.
    } finally {
      endPerformanceOperation();
    }
  }

  private async playVideoStepAsync(run: ActiveScriptRun, stepName: string): Promise<void> {
    const play = this.m_host.playCurrentStepVideo;
    if (!play) {
      console.warn("[WorkflowScriptRunner] play_video skipped — this host has no media playback surface.");
      return;
    }

    const deadline = performance.now() + K_VIDEO_READY_TIMEOUT_MS;
    let outcome: WorkflowMediaControlResult["outcome"] = "unavailable";
    while (!run.cancelled) {
      const result = play(stepName);
      outcome = result.outcome;
      if (outcome === "ok") return;
      if (outcome !== "unavailable" || performance.now() >= deadline) break;
      await new Promise<void>((resolve) => window.setTimeout(resolve, K_CANCEL_POLL_MS));
    }

    if (!run.cancelled) {
      console.warn(
        `[WorkflowScriptRunner] play_video on step '${stepName}' could not start (${outcome}).`,
      );
    }
  }

  /**
   * The mode this script will actually run under. A declared mode with no
   * scene state to capture against (headless host) degrades to `none` rather
   * than promising a revert that cannot happen.
   */
  private resolveRevertMode(script: WorkflowScript): WorkflowScriptRevert {
    if (script.revert === "none") return "none";
    if (this.m_revert) return script.revert;
    console.warn(
      `[WorkflowScriptRunner] Script '${script.id}' declares revert '${script.revert}' but no project is attached — steps apply without revert.`,
    );
    return "none";
  }

  /**
   * `on_action_finish` restores now; `on_scene_change` hands the bag to the
   * pending queue, where it waits for the learner to leave the scene the
   * script ran in (Unity `QueuePendingSceneChangeRevert`).
   */
  /**
   * Settles ONE run's revert, at most once. Two paths reach it — a stop that
   * does not want to wait for the loop, and the loop's own `finally` — and a
   * double restore would put back state a NEWER script has since captured as
   * its own "before".
   */
  private settleRunRevert(run: ActiveScriptRun): void {
    if (run.revertSettled) return;
    run.revertSettled = true;
    try {
      this.settleRevert(run.revertMode, run.ledger);
    } finally {
      run.resolveRevertSettlement();
    }
  }

  private settleRevert(mode: WorkflowScriptRevert, ledger: WorkflowRevertLedger | null): void {
    if (!ledger || !this.m_revert || !ledger.hasRestorableState) return;

    if (mode === "on_scene_change") {
      this.m_pendingSceneChangeReverts.mergeLedger(ledger);
      return;
    }
    this.queueRevert((deps) => ledger.restoreAllAsync(deps));
  }

  /** Serializes restores so two of them can never interleave their writes. */
  private queueRevert(restore: (deps: WorkflowRevertDependencies) => Promise<void>): void {
    const deps = this.m_revert;
    if (!deps) return;
    this.m_revertChain = this.m_revertChain
      .then(() => restore(deps))
      .catch((e) => {
        console.warn("[WorkflowScriptRunner] Script revert failed:", e);
      });
  }

  /**
   * Drains `on_scene_change` bags for the scene being LEFT (Unity does the same
   * from `ProjectModelSlim`'s active-scene switch). A null scene is a fallback
   * drain; normal project unload restores through the awaited barrier above,
   * before the project model clears the rows this state needs.
   */
  private handleActiveSceneChanged(nextSceneId: string | null): void {
    const departed = this.m_lastActiveSceneId;
    this.m_lastActiveSceneId = nextSceneId;

    if (nextSceneId === null) {
      const pending = this.m_pendingSceneChangeReverts;
      this.queueRevert((deps) => pending.restoreAllAsync(deps));
      return;
    }
    if (!departed || departed === nextSceneId) return;

    const state = this.m_pendingSceneChangeReverts.take(departed);
    if (state) this.queueRevert((deps) => state.restoreAsync(deps));
  }

  /** Port of the step conversion checks in Unity `TryBuildActionData` (same error strings). */
  private static validateSteps(script: WorkflowScript): string | null {
    if (script.steps.length === 0) {
      return `error: script_has_no_steps (script "${script.id}")`;
    }

    let stepNumber = 0;
    for (const step of script.steps) {
      stepNumber++;
      if (step.isSay) continue;

      // A step that is neither `say:` nor `tool:` is preserved verbatim by
      // the codec (`invalidRaw`) and reported by `validate()`; refuse to play
      // it with a step-numbered reason instead of `unknown_tool ""`.
      if (step.isInvalid || step.tool.trim().length === 0) {
        return `error: step_is_not_a_say_or_tool (script "${script.id}" step ${stepNumber})`;
      }

      const toolId = step.tool.trim().toLowerCase();
      if (!K_VALID_TOOL_IDS.has(toolId)) {
        return `error: unknown_tool "${step.tool}" (script "${script.id}" step ${stepNumber})`;
      }

      // A script belongs to a step, so it must not move the run out from under
      // itself: the step it lands on plays its own on-start script immediately,
      // and two steps navigating to each other would ping-pong with no learner
      // input at all. Moving the run is the guide's CONVERSATIONAL tool and the
      // panel's buttons; authored branching is the step's `triggers` list.
      if (toolId === AgentToolNames.GoToWorkflowStep.toLowerCase()) {
        return (
          `error: script_cannot_navigate_the_run (script "${script.id}" step ${stepNumber} calls ` +
          `${AgentToolNames.GoToWorkflowStep}; branch with the step's triggers list instead)`
        );
      }

      // A read-only lookup would execute and return its text to nobody: a
      // learner mid-lesson is not reading a tool result. Refusing here is what
      // stops a script silently doing nothing for a whole step. `validate()`
      // says the same thing as a "Not a script command" issue the panel shows
      // — this is the belt for a document that reached playback anyway.
      if (isScriptContextTool(toolId)) {
        return (
          `error: script_step_is_a_lookup (script "${script.id}" step ${stepNumber} calls ` +
          `${step.tool}; resolve the target while authoring and write the resolved value into args)`
        );
      }

      // Arguments are a YAML mapping, so they are already an object or absent.
    }

    return null;
  }

  private static isDoNotWaitStep(step: WorkflowScript["steps"][number]): boolean {
    return !step.isSay
      && step.tool.trim().toLowerCase() === AgentToolNames.DoNotWait.toLowerCase();
  }

  private static isWaitForStep(step: WorkflowScript["steps"][number]): boolean {
    return !step.isSay
      && !step.isInvalid
      && step.tool.trim().toLowerCase() === AgentToolNames.WaitFor.toLowerCase();
  }

  private static isPlayVideoStep(step: WorkflowScript["steps"][number]): boolean {
    return !step.isSay
      && !step.isInvalid
      && step.tool.trim().toLowerCase() === AgentToolNames.PlayVideo.toLowerCase();
  }

  /** The play panel's label for a wait, so the learner is not looking at a stalled step. */
  private static describeWait(args: Record<string, unknown>): string {
    if (WorkflowScriptRunner.readWaitTrigger(args) === "delay") {
      const seconds = WorkflowScriptRunner.readDelaySeconds(args);
      return `Waiting ${seconds}s`;
    }
    return "Waiting for you";
  }

  private static readWaitTrigger(args: Record<string, unknown>): "delay" | "user_response" {
    const raw = args["trigger"];
    return typeof raw === "string" && raw.trim().toLowerCase() === "delay"
      ? "delay"
      : "user_response";
  }

  /**
   * Clamped into `(0, K_MAX_WAIT_SECONDS]`. An authored delay is a number
   * somebody typed, and a script that parks a learner for an hour is a typo
   * rather than an intention — but the step still has to end, so this clamps
   * rather than refusing.
   */
  private static readDelaySeconds(args: Record<string, unknown>): number {
    const raw = args["delaySeconds"];
    const parsed = typeof raw === "number" ? raw : Number(String(raw ?? "").trim());
    if (!Number.isFinite(parsed) || parsed <= 0) return K_DEFAULT_WAIT_SECONDS;
    return Math.min(parsed, K_MAX_WAIT_SECONDS);
  }

  /**
   * Parks the script until the learner acts, or for a fixed delay.
   *
   * Both paths race the run's cancellation, so navigating away (Next, Back, a
   * trigger, Stop) unwinds the wait immediately instead of leaving the revert
   * to settle a minute later — the same reason `stopSpeaking` exists.
   *
   * A `user_response` wait ALSO carries a ceiling. A learner who walks away
   * would otherwise hold the script — and its revert — open for the tab's
   * life, and a workflow that never resumes is worse than one that moves on.
   */
  /** The host's turn counter, or 0 when it does not keep one. */
  private readUserTurnCount(): number {
    return this.m_host.getUserTurnCount?.() ?? 0;
  }

  private async playWaitStepAsync(
    run: ActiveScriptRun,
    args: Record<string, unknown>,
    sinceTurnCount: number,
  ): Promise<void> {
    const trigger = WorkflowScriptRunner.readWaitTrigger(args);

    if (trigger === "delay") {
      const seconds = WorkflowScriptRunner.readDelaySeconds(args);
      await this.raceCancellation(run, (signal) => delayAsync(seconds * 1000, signal));
      return;
    }

    const waitForUserTurn = this.m_host.waitForUserTurnAsync;
    if (!waitForUserTurn) {
      // No seam (headless host, or a transport with no user-turn signal).
      // Degrade to a short pause rather than either hanging or racing straight
      // past a question the learner was just asked.
      console.warn(
        "[WorkflowScriptRunner] wait_for user_response: host has no user-turn signal; pausing briefly instead.",
      );
      await this.raceCancellation(run, (signal) =>
        delayAsync(K_DEFAULT_WAIT_SECONDS * 1000, signal),
      );
      return;
    }

    let turnArrived = false;
    await this.raceCancellation(run, async (signal) => {
      turnArrived = await waitForUserTurn.call(
        this.m_host,
        sinceTurnCount,
        K_USER_RESPONSE_TIMEOUT_MS,
        signal,
      );
    });

    // The learner's turn is SUBMITTED before it is answered, so resuming on it
    // alone puts the script's next say into the same window as the agent's
    // reply — two `response.create`s on one socket, and the second is dropped,
    // which loses a scripted line silently. Let the answer finish first. Only
    // after a real turn: a timeout or a cancel has no reply coming, and this
    // would spend its grace window waiting for one.
    if (turnArrived && !run.cancelled) {
      await this.m_host.waitForResponseIdleAsync(K_SAY_TIMEOUT_MS);
    }
  }

  /** Runs `work` with an abort signal that fires as soon as the run is cancelled. */
  private async raceCancellation(
    run: ActiveScriptRun,
    work: (signal: AbortSignal) => Promise<void>,
  ): Promise<void> {
    const controller = new AbortController();
    const poll = window.setInterval(() => {
      if (run.cancelled) controller.abort();
    }, K_CANCEL_POLL_MS);
    if (run.cancelled) controller.abort();

    try {
      await work(controller.signal);
    } catch (e) {
      // An aborted wait is the normal cancellation path, not a failure — it
      // must not fall through to the loop's catch and report `failed`.
      if (!controller.signal.aborted) throw e;
    } finally {
      window.clearInterval(poll);
    }
  }

  /** Unity `ShouldOverlapReplayStep`: `do_not_wait` affects the step before it. */
  private static shouldOverlapStep(script: WorkflowScript, currentIndex: number): boolean {
    const next = script.steps[currentIndex + 1];
    return !!next && WorkflowScriptRunner.isDoNotWaitStep(next);
  }

  private static isFailureResult(result: string): boolean {
    const normalized = result.trim().toLowerCase();
    return normalized.startsWith("error:")
      || normalized.startsWith("tool_error:")
      || normalized.startsWith("tool_not_supported:");
  }
}

/**
 * Keeps a run's outstanding-tool count honest, including across the
 * `do_not_wait` overlap path, so a stop can tell whether reverting right now
 * would race a write that has been dispatched but has not landed.
 *
 * A run skipped for that reason is not orphaned: its loop is either awaiting
 * this task or will reach its top-of-loop cancel check once this task settles,
 * and the loop's `finally` settles the revert either way.
 *
 * Deliberately a free function — it is bookkeeping on the run object, not
 * behaviour of the runner.
 */
function trackToolStep(run: ActiveScriptRun, task: Promise<void>): Promise<void> {
  run.pendingToolCount++;
  return task.finally(() => {
    run.pendingToolCount--;
  });
}
